'use client'

import Image from 'next/image'
import { Card } from '@/components/ui/card'
import { useEffect, useRef } from 'react'
import type { FC } from 'react'
import { clinicalTeam } from '@/lib/clinicalteam'
import type { TeamMember } from '@/types/doctor'
import {
  usesPsychologistCard,
  formatRole,
  normalizeExpertise,
} from '@/types/doctor'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import PsychologistCard from '@/components/team/psychologistCard'

gsap.registerPlugin(ScrollTrigger)

/* ────────────────────────────
   Reusable Team-Member Card (for non-psychologists)
   ──────────────────────────── */
export const TeamMemberCard: FC<{
  member: TeamMember
  onClickAction: () => void
}> = ({ member, onClickAction }) => (
  <Card
    onClick={onClickAction}
    className="
      relative cursor-pointer overflow-hidden rounded-xl
      transition-transform duration-300
      hover:-translate-y-1 will-change-transform
      focus-within:ring-2 focus-within:ring-white/50 focus-within:ring-offset-2
      focus-within:ring-offset-[#191A23]
    "
    tabIndex={0}
    role="button"
    aria-label={`View bio for ${member.name}`}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClickAction()
      }
    }}
  >
    {/* full-bleed photo */}
    <div className="relative h-64 sm:h-72 md:h-80 w-full">
      <Image
        src={member.imageUrl}
        alt={`${member.name}, ${member.title}`}
        fill
        sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"
        className="object-cover"
        priority={false}
      />
    </div>

    {/* name + title overlay */}
    <div
      className="
        absolute inset-x-0 bottom-0
        bg-gradient-to-t from-black/75 via-black/45 to-transparent
        px-4 py-3 sm:py-4
      "
    >
      <h3 className="text-base sm:text-lg font-semibold text-white leading-tight">
        {member.name}
      </h3>
      <p className="text-xs sm:text-sm text-gray-300 mt-0.5">{member.title}</p>
      <p className="text-[11px] sm:text-xs text-gray-400 mt-1">
        {formatRole(member.role)}
      </p>
    </div>
  </Card>
)

/* ────────────────────────────
   Clinical-Team Section
   ──────────────────────────── */
interface ClinicalTeamSectionProps {
  onViewBio: (member: TeamMember) => void
}

const ClinicalTeamSection: FC<ClinicalTeamSectionProps> = ({ onViewBio }) => {
  const gridRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const gridEl = gridRef.current
    if (!gridEl) return

    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (prefersReduced) return

    const cards = gridEl.querySelectorAll('[data-team-card]')
    gsap.set(cards, { opacity: 0, y: 30 })

    const animation = gsap.to(cards, {
      opacity: 1,
      y: 0,
      ease: 'power2.out',
      duration: 0.6,
      stagger: 0.08,
      scrollTrigger: {
        trigger: gridEl,
        start: 'top 80%',
      },
    })

    return () => {
      animation.kill()
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === gridEl) st.kill()
      })
    }
  }, [])

  /**
   * Render a team member card based on their role using type guards
   */
  const renderTeamMemberCard = (member: TeamMember) => {
    if (usesPsychologistCard(member)) {
      const expertise = normalizeExpertise(member.expertise)

      return (
        <PsychologistCard
          member={{
            name: member.name,
            title: member.title,
            credentials: member.credentials,
            imageUrl: member.imageUrl,
            specialty: member.specialty,
            yearsExperience: member.yearsExperience,
            expertise: [...expertise.children, ...expertise.adults],
          }}
          onViewBio={() => onViewBio(member)}
        />
      )
    }

    return (
      <TeamMemberCard member={member} onClickAction={() => onViewBio(member)} />
    )
  }

  return (
    <section
      className="py-10 sm:py-12 md:py-16 w-full bg-[#191A23]"
      aria-labelledby="clinical-team-heading"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <header className="mb-8 sm:mb-10 md:mb-12 text-center sm:text-left">
          <h2
            id="clinical-team-heading"
            className="text-[26px] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight"
          >
            Our Clinical Team
          </h2>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-white/90 max-w-3xl mx-auto sm:mx-0">
            Our experienced psychologists and therapists specialize in various
            areas of mental health to provide comprehensive care.
          </p>
        </header>

        <div
          ref={gridRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-8"
          role="list"
          aria-label="Clinical team members"
        >
          {clinicalTeam.map((member) => (
            <div
              key={member.id}
              data-team-card
              role="listitem"
              aria-label={`${member.name}, ${formatRole(member.role)}`}
            >
              {renderTeamMemberCard(member)}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ClinicalTeamSection
